import { useEffect, useRef, useState, useCallback } from "react";
import {
    idForElement,
    isImage,
    EDITABLE_SELECTOR,
} from "../builder/builderUtils";
import {
    loadOverrides,
    applyOverrides,
    saveOverrides,
    login,
    getToken,
    setToken,
    checkEditorSession,
} from "../builder/contentStore";
import "../builder/visualEditor.css";

export default function VisualEditor() {
    const [active, setActive] = useState(false);
    const [authed, setAuthed] = useState(false);
    const [editorVerified, setEditorVerified] = useState(false); // backend confirmed editor
    const [selected, setSelected] = useState(null); // { id, el, type }
    const [draft, setDraft] = useState("");
    const [status, setStatus] = useState("");
    const [count, setCount] = useState(0);
    const [showLogin, setShowLogin] = useState(false);
    const [password, setPassword] = useState("");
    const [loginErr, setLoginErr] = useState("");
    const overridesRef = useRef({});

    // Hidden entry for first-time admins: visiting ?editor-access reveals the
    // login prompt. Without it (and without a valid editor session) the public
    // never sees the Edit button.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("editor-access") === "1") setShowLogin(true);
    }, []);

    // On load: if we have a token, ask the backend whether it is an editor
    // session. The Edit button is shown ONLY after this confirms true.
    useEffect(() => {
        if (!getToken()) return;
        checkEditorSession().then((ok) => {
            setEditorVerified(ok);
            setAuthed(ok);
        });
    }, []);

    // Always load + apply saved overrides on mount so changes persist on the
    // live page even when the editor is not active.
    useEffect(() => {
        let cancelled = false;
        loadOverrides().then((ov) => {
            if (cancelled) return;
            overridesRef.current = ov;
            applyOverrides(ov);
            setCount(Object.keys(ov).length);
        });
        return () => { cancelled = true; };
    }, []);

    // when active: attach MutationObserver + cursor styling
    useEffect(() => {
        if (!active) return;
        const reapply = () => setTimeout(() => applyOverrides(overridesRef.current), 400);
        const ro = new MutationObserver(reapply);
        ro.observe(document.body, { childList: true, subtree: true });
        document.body.classList.add("ve-active");
        return () => {
            ro.disconnect();
            document.body.classList.remove("ve-active");
        };
    }, [active]);

    const onElementClick = useCallback((e) => {
        const el = e.target.closest(EDITABLE_SELECTOR);
        if (!el || el.closest("[data-ve-builder]")) return;
        e.preventDefault();
        e.stopPropagation();
        const id = idForElement(el);
        const img = isImage(el);
        const type = img ? "src" : "text";
        const value = img ? el.getAttribute("src") : firstText(el);
        setSelected({ id, el, type });
        setDraft(value || "");
    }, []);

    useEffect(() => {
        if (!active || !authed) return;
        document.addEventListener("click", onElementClick, true);
        return () => document.removeEventListener("click", onElementClick, true);
    }, [active, authed, onElementClick]);

    const commit = () => {
        if (!selected) return;
        overridesRef.current = {
            ...overridesRef.current,
            [selected.id]: { type: selected.type, value: draft },
        };
        if (selected.type === "src" && selected.el.tagName === "IMG") {
            selected.el.src = draft;
        } else {
            applyText(selected.el, draft);
        }
        setCount(Object.keys(overridesRef.current).length);
        setSelected(null);
        setStatus("Change staged — click Save to make it permanent");
    };

    const doLogin = async () => {
        setLoginErr("");
        try {
            await login(password);
            setAuthed(true);
            setEditorVerified(true);
            setShowLogin(false);
            setPassword("");
        } catch {
            setLoginErr("Invalid password");
        }
    };

    const save = async () => {
        if (!authed) {
            setShowLogin(true);
            return;
        }
        if (!Object.keys(overridesRef.current).length) {
            setStatus("No changes staged yet");
            setTimeout(() => setStatus(""), 2000);
            return;
        }
        setStatus("Saving…");
        const ok = await saveOverrides(overridesRef.current);
        setStatus(ok ? "Saved permanently to file ✓" : "Save failed (not authorized)");
        setTimeout(() => setStatus(""), 2500);
    };

    const logout = () => {
        setToken("");
        setAuthed(false);
        setEditorVerified(false);
        setActive(false);
    };

    const reset = () => {
        overridesRef.current = {};
        setCount(0);
        setStatus("Reset — reloading…");
        setTimeout(() => window.location.reload(), 600);
    };

    // Login modal is renderable at any time (top-level), so first-time admins
    // hitting ?editor-access=1 see it even before they are verified.
    // The Edit entry button is shown ONLY to verified editors.
    const loginModal = showLogin && (
        <div className="ve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ve-modal-head">Editor Login (required)</div>
            <input
                className="ve-input"
                type="password"
                value={password}
                autoFocus
                placeholder="Editor password"
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doLogin()}
            />
            {loginErr && <div className="ve-status" style={{ color: "#e08" }}>{loginErr}</div>}
            <div className="ve-modal-actions">
                        <button type="button" className="ve-btn ve-btn-ghost" onClick={() => setShowLogin(false)}>
                            Cancel
                        </button>
                        <button type="button" className="ve-btn ve-btn-primary" onClick={doLogin}>
                            Login
                        </button>
            </div>
        </div>
    );

    if (!active) {
        // Public (unverified) visitors: render nothing (no Edit button, no modal
        // unless they explicitly opened the hidden ?editor-access entry).
        if (!editorVerified && !showLogin) return null;
        return (
            <div data-ve-builder className="ve-root">
                {loginModal}
                {editorVerified && (
                    <button
                        type="button"
                        data-ve-builder
                        className="ve-fab"
                        onClick={() => setActive(true)}
                        title="Open Visual Builder"
                    >
                        ✎ Edit
                    </button>
                )}
            </div>
        );
    }

    return (
        <div data-ve-builder className="ve-root">
            {loginModal}

            {selected && (
                <div className="ve-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ve-modal-head">
                        Editing <code>{selected.type === "src" ? "image src" : "text"}</code>
                        <span className="ve-id">{selected.id}</span>
                    </div>
                    {selected.type === "src" ? (
                        <input
                            className="ve-input"
                            value={draft}
                            autoFocus
                            onChange={(e) => setDraft(e.target.value)}
                            placeholder="/image.png or https://…"
                        />
                    ) : (
                        <textarea
                            className="ve-textarea"
                            value={draft}
                            autoFocus
                            onChange={(e) => setDraft(e.target.value)}
                        />
                    )}
                    <div className="ve-modal-actions">
                        <button type="button" className="ve-btn ve-btn-ghost" onClick={() => setSelected(null)}>
                            Cancel
                        </button>
                        <button type="button" className="ve-btn ve-btn-primary" onClick={commit}>
                            Apply
                        </button>
                    </div>
                </div>
            )}

            <div className="ve-toolbar">
                <span className="ve-logo">Visual Builder</span>
                <span className="ve-count">{count} edits</span>
                <span className={`ve-auth ${authed ? "ve-auth-on" : ""}`}>
                    {authed ? "● authed" : "○ locked"}
                </span>
                {status && <span className="ve-status">{status}</span>}
                <span className="ve-spacer" />
                {authed ? (
                    <button type="button" className="ve-btn ve-btn-ghost" onClick={logout}>
                        Logout
                    </button>
                ) : (
                    <button type="button" className="ve-btn ve-btn-ghost" onClick={() => setShowLogin(true)}>
                        Login
                    </button>
                )}
                <button type="button" className="ve-btn ve-btn-ghost" onClick={reset}>
                    Reset
                </button>
                <a
                    className="ve-btn ve-btn-ghost"
                    href={window.location.pathname}
                    onClick={(e) => {
                        e.preventDefault();
                        setActive(false);
                    }}
                >
                    Exit
                </a>
                <button type="button" className="ve-btn ve-btn-save" onClick={save}>
                    Save
                </button>
            </div>

            <style>{`body.ve-active *{cursor:pointer !important;}
body.ve-active [data-ve-builder]{cursor:default !important;}`}</style>
        </div>
    );
}

function firstText(el) {
    if (el.children.length === 0) return el.textContent;
    const node = Array.from(el.childNodes).find(
        (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length
    );
    return node ? node.textContent : el.textContent;
}

function applyText(el, text) {
    if (el.children.length === 0) {
        el.textContent = text;
    } else {
        let node = Array.from(el.childNodes).find(
            (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length
        );
        if (node) node.textContent = text;
        else el.textContent = text;
    }
}
